import React, { useState } from 'react';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { ODataOptions, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import Landman1099 from 'src/models/personnel/Landman1099';
import Lm1099Search, { ILm1099SearchCriteria } from '../../components/Lm1099Search';
import Lm1099Grid from '../../components/Lm1099Grid';
import DialogList from 'src/dialogs';

interface ILm1099MaintenanceDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Ports L1099_LM.fmt's Standard/LM_1099/Seed screens into one dialog, mirroring
// Ap1099MaintenanceDialog.tsx. Company Maintenance/Export/Reports are left out for now - unlike AP's
// ap_1099.fmt, those Landman sub-screens haven't been screenshotted yet and the report RDFs
// (EMP_1099_V2/EMPA1099_V2/EMP1099BR_V2) haven't been exported to .xml, so there isn't enough source
// material to port them correctly yet.
const Lm1099MaintenanceDialog = (props: ILm1099MaintenanceDialogProps) => {
    const { get, put, post, response } = useFetch();
    const [rows, setRows] = useState<Array<Landman1099>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [seeding, setSeeding] = useState<boolean>(false);
    const [seedYear, setSeedYear] = useState<string>(`${new Date().getFullYear()}`);
    const [tinMatchingYear, setTinMatchingYear] = useState<string>(`${new Date().getFullYear()}`);
    const [generatingTinMatchingFile, setGeneratingTinMatchingFile] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; color?: string } | null>(null);

    const fetchLandman1099 = async (criteria: ILm1099SearchCriteria) => {
        setLoading(true);
        setMessage(null);

        const tableConfig: ITableConfig = { rowCount: 250, idField: 'LM_NO', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        options.addOrderBy('TaxYear', ODataOrderByDirection.Descending);

        // lmNo/taxYear are sent as plain query params (not $filter) - GetLandman1099 takes them as
        // explicit parameters and always ANDs them together, sidestepping the same OR/AND filter bug
        // fixed for GetAp1099.
        const extraParams: string[] = [];
        if (criteria.lmNo) {
            extraParams.push(`lmNo=${encodeURIComponent(criteria.lmNo)}`);
        }
        if (criteria.taxYear) {
            extraParams.push(`taxYear=${encodeURIComponent(criteria.taxYear)}`);
        }
        const queryString = options.toString() + (extraParams.length > 0 ? (options.toString() ? '&' : '?') + extraParams.join('&') : '');

        const result = await get(`/v1.0/landmen/lm1099${queryString}`);

        if (response.ok) {
            setRows(result?.items || []);
        } else {
            setMessage({ text: 'Unable to load 1099 records.', color: 'rgba(255,0,0,0.5)' });
        }
        setLoading(false);
    };

    const handleSaveRows = async (updatedRows: Array<Landman1099>) => {
        setSaving(true);
        let anyFailed = false;

        for (const row of updatedRows) {
            await put('/v1.0/landmen/lm1099', row);
            if (!response.ok) {
                anyFailed = true;
            }
        }

        setSaving(false);
        setMessage(anyFailed
            ? { text: 'Some rows failed to save.', color: 'rgba(255,0,0,0.5)' }
            : { text: 'Changes saved.' });

        if (!anyFailed && rows.length > 0) {
            const distinctYears = Array.from(new Set(rows.map((r) => r.taxYear)));
            if (distinctYears.length === 1) {
                fetchLandman1099({ taxYear: `${distinctYears[0]}` });
            }
        }
    };

    const handleSeed = async () => {
        const taxYear = Number(seedYear);
        if (!taxYear) {
            setMessage({ text: 'Enter a Tax Year to seed.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        const confirmed = window.confirm(
            `This will delete and rebuild all ${taxYear} 1099 records from commission history. Continue?`
        );
        if (!confirmed) {
            return;
        }

        setSeeding(true);
        await post(`/v1.0/landmen/lm1099/seed/${taxYear}`, {});
        setSeeding(false);

        if (response.ok) {
            setMessage({ text: `${taxYear} 1099 records rebuilt.` });
            fetchLandman1099({ taxYear: `${taxYear}` });
        } else {
            setMessage({ text: 'Unable to seed 1099 records.', color: 'rgba(255,0,0,0.5)' });
        }
    };

    const handleGenerateTinMatchingFile = async () => {
        const taxYear = Number(tinMatchingYear);
        if (!taxYear) {
            setMessage({ text: 'Enter a Tax Year for the TIN Matching File.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        setGeneratingTinMatchingFile(true);
        await get(`/v1.0/landmen/lm1099/tinmatchingfile/${taxYear}`);

        if (response.ok) {
            const blob = await response.blob();
            downloadBlobResponseAsFile(blob, 'TINMatching.txt');
        } else {
            setMessage({ text: 'Unable to generate TIN Matching File.', color: 'rgba(255,0,0,0.5)' });
        }
        setGeneratingTinMatchingFile(false);
    };

    return (
        <Dialog title="1099 Maintenance" dialogName={props.dialogName} bodyStyle={{ width: '90%', maxWidth: '1400px' }}>
            <Lm1099Search onSearch={fetchLandman1099} />

            {message && (
                <div style={{ padding: '.5rem', color: message.color ? '#a00' : '#060' }}>{message.text}</div>
            )}

            <Panel backgroundColor="#D3D3D3">
                <Panel panelTitle="1099 Records">
                    <div style={{ padding: '.5rem', maxHeight: '30vh', overflowY: 'auto' }}>
                        {loading ? <div className="bold-centered">Loading...</div> : (
                            <Lm1099Grid rows={rows} onSaveRows={handleSaveRows} saving={saving} />
                        )}
                    </div>
                </Panel>
            </Panel>

            <div className="form-row">
                <Panel backgroundColor="#D3D3D3" style={{ flex: 1, marginRight: '.5rem' }}>
                    <Panel panelTitle="Seed">
                        <div className="form-row" style={{ padding: '.5rem', alignItems: 'center' }}>
                            <label style={{ marginRight: '.5rem' }}>Tax Year</label>
                            <input
                                type="number"
                                value={seedYear}
                                onChange={(e) => setSeedYear(e.currentTarget.value)}
                                style={{ width: '6rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '1rem' }}
                            />
                            <Button
                                text={seeding ? 'Seeding...' : 'Seed'}
                                className="blue-hover-button"
                                disabled={seeding}
                                style={{ width: '8rem' }}
                                onClick={() => handleSeed()}
                            />
                        </div>
                    </Panel>
                </Panel>

                <Panel backgroundColor="#D3D3D3" style={{ flex: 1 }}>
                    <Panel panelTitle="TIN Matching File">
                        <div className="form-row" style={{ padding: '.5rem', alignItems: 'center' }}>
                            <label style={{ marginRight: '.5rem' }}>Tax Year</label>
                            <input
                                type="number"
                                value={tinMatchingYear}
                                onChange={(e) => setTinMatchingYear(e.currentTarget.value)}
                                style={{ width: '6rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '1rem' }}
                            />
                            <Button
                                text={generatingTinMatchingFile ? 'Generating...' : 'Generate'}
                                className="blue-hover-button"
                                disabled={generatingTinMatchingFile}
                                style={{ width: '8rem' }}
                                onClick={() => handleGenerateTinMatchingFile()}
                            />
                        </div>
                    </Panel>
                </Panel>
            </div>

            <div className="form-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                <Button
                    text="Reports"
                    className="blue-hover-button"
                    style={{ width: 'auto', paddingLeft: '1rem', paddingRight: '1rem', marginRight: '1rem', whiteSpace: 'nowrap' }}
                    onClick={() => props.showDialog(DialogList.LM_1099_REPORT)}
                />
                <Button
                    text="Company Maintenance"
                    className="blue-hover-button"
                    style={{ width: 'auto', paddingLeft: '1rem', paddingRight: '1rem', marginRight: '1rem', whiteSpace: 'nowrap' }}
                    onClick={() => props.showDialog(DialogList.COMPANY_MAINTENANCE)}
                />
                <Button
                    text="Close"
                    className="blue-hover-button"
                    style={{ width: '8rem' }}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default Lm1099MaintenanceDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/pay/Lm1099MaintenanceDialog.tsx